/**
 * Fuel Station Page
 * Handles all interactions for the page-based coffee fuel station.
 * 
 * Features:
 * - Coffee product switching (updates gallery, price, variants)
 * - Grind/Size selection (updates variant ID)
 * - 4lbs quantity doubling logic
 * - Subscription toggle (attaches/detaches selling_plan_id)
 * - Cart form submission
 */

class FuelStationPage extends HTMLElement {
    constructor() {
        super();
        this.sectionId = this.dataset.sectionId;
        this.currentHandle = this.dataset.currentHandle;
        this.productsData = null;
        this.selectedGrind = null;
        this.selectedSize = null;
        this.is4lbs = false;
        this.autoshipEnabled = false;
        this.currentBasePrice = 0;
    }

    connectedCallback() {
        this.loadProductsData();
        this.bindEvents();
        this.initializeState();
    }

    loadProductsData() {
        const dataEl = document.getElementById(`fs-products-data-${this.sectionId}`);
        if (dataEl) {
            try {
                this.productsData = JSON.parse(dataEl.textContent);
                console.log('[FuelStation] Loaded products data:', this.productsData);
            } catch (e) {
                console.error('[FuelStation] Failed to parse products data:', e);
            }
        }
    }

    bindEvents() {
        // Fuel selection (coffee switching)
        this.querySelectorAll('[data-action="switch-fuel"]').forEach(btn => {
            btn.addEventListener('click', (e) => this.handleFuelSwitch(e));
        });

        // Grind selection
        this.querySelectorAll('[data-action="select-grind"]').forEach(btn => {
            btn.addEventListener('click', (e) => this.handleGrindSelect(e));
        });

        // Size selection
        this.querySelectorAll('[data-action="select-size"]').forEach(btn => {
            btn.addEventListener('click', (e) => this.handleSizeSelect(e));
        });

        // Quantity controls
        this.querySelector('[data-action="quantity-decrease"]')?.addEventListener('click', () => this.adjustQuantity(-1));
        this.querySelector('[data-action="quantity-increase"]')?.addEventListener('click', () => this.adjustQuantity(1));

        // Autoship toggle
        const autoshipToggle = document.getElementById(`fs-autoship-toggle-${this.sectionId}`);
        if (autoshipToggle) {
            autoshipToggle.addEventListener('change', (e) => this.handleAutoshipToggle(e));
        }

        // Form submission
        const form = document.getElementById(`fs-form-${this.sectionId}`);
        if (form) {
            form.addEventListener('submit', (e) => this.handleSubmit(e));
        }

        // Description toggle
        this.querySelector('[data-action="toggle-description"]')?.addEventListener('click', (e) => this.handleDescriptionToggle(e));

        // Gallery thumbnails
        this.querySelectorAll('.fs-gallery-thumb').forEach(thumb => {
            thumb.addEventListener('click', (e) => this.handleThumbClick(e));
        });
    }

    handleThumbClick(e) {
        const thumb = e.currentTarget;
        const mediaSrc = thumb.dataset.mediaSrc;
        const mainImg = document.getElementById(`fs-main-img-${this.sectionId}`);

        if (mainImg && mediaSrc) {
            mainImg.src = mediaSrc;
        }

        // Update active state
        this.querySelectorAll('.fs-gallery-thumb').forEach(t => t.classList.remove('is-active'));
        thumb.classList.add('is-active');
    }

    initializeState() {
        // Get initial grind selection
        const selectedGrind = this.querySelector('.fs-options--grind .is-selected');
        if (selectedGrind) {
            this.selectedGrind = selectedGrind.dataset.optionValue;
        }

        // Get initial autoship state
        const autoshipToggle = document.getElementById(`fs-autoship-toggle-${this.sectionId}`);
        if (autoshipToggle) {
            this.autoshipEnabled = autoshipToggle.checked;
        }

        // Initial price setup
        const initialVariant = this.productsData?.products?.[this.currentHandle]?.variants?.find(v => v.available) || this.productsData?.products?.[this.currentHandle]?.variants?.[0];
        if (initialVariant) {
            this.currentBasePrice = initialVariant.price;
        }

        console.log('[FuelStation] Initialized with:', {
            handle: this.currentHandle,
            grind: this.selectedGrind,
            size: this.selectedSize,
            autoship: this.autoshipEnabled,
            price: this.currentBasePrice
        });
    }

    // =========================================
    // FUEL SWITCHING
    // =========================================

    handleFuelSwitch(e) {
        e.preventDefault();
        const btn = e.currentTarget;
        const handle = btn.dataset.fuelHandle;

        if (handle === this.currentHandle) {
            console.log('[FuelStation] Already on this product');
            return;
        }

        if (!this.productsData?.products?.[handle]) {
            console.error('[FuelStation] Product data not found for:', handle);
            return;
        }

        console.log('[FuelStation] Switching to:', handle);

        // Update visual selection
        this.querySelectorAll('[data-action="switch-fuel"]').forEach(b => {
            b.classList.toggle('is-selected', b.dataset.fuelHandle === handle);
        });

        // Update current handle
        this.currentHandle = handle;
        this.dataset.currentHandle = handle;

        // Update UI with new product data
        this.updateProductUI(this.productsData.products[handle]);
    }

    updateProductUI(productData) {
        // Update gallery image
        const galleryImg = this.querySelector('.fs-gallery-img');
        if (galleryImg && productData.image) {
            galleryImg.src = productData.image;
            galleryImg.alt = productData.title;
        }

        // Update price display
        this.updateVariant(productData);

        // Update Judge.me badge if it exists and is set to "current product only"
        const jdgmBadge = this.querySelector('.jdgm-preview-badge[data-fs-current-product-only="true"]');
        if (jdgmBadge) {
            jdgmBadge.dataset.id = productData.id;
            if (window.jdgm && typeof window.jdgm.customizeBadge === 'function') {
                window.jdgm.customizeBadge();
            }
            if (window.jdgm && typeof window.jdgm.renderAll === 'function') {
                window.jdgm.renderAll();
            }
        }

        // Update autoship selling plans
        this.updateSellingPlans(productData);
    }

    // =========================================
    // GRIND SELECTION
    // =========================================

    handleGrindSelect(e) {
        e.preventDefault();
        const btn = e.currentTarget;
        const value = btn.dataset.optionValue;

        // Update visual selection
        this.querySelectorAll('.fs-options--grind .fs-option-card').forEach(b => {
            b.classList.remove('is-selected');
        });
        btn.classList.add('is-selected');

        this.selectedGrind = value;
        console.log('[FuelStation] Selected grind:', value);

        this.updateVariantFromSelection();
    }

    // =========================================
    // SIZE SELECTION
    // =========================================

    handleSizeSelect(e) {
        e.preventDefault();
        const btn = e.currentTarget;
        const specialSize = btn.dataset.specialSize;
        const value = btn.dataset.optionValue;

        // Update visual selection
        this.querySelectorAll('.fs-options--size .fs-option-card').forEach(b => {
            b.classList.remove('is-selected');
        });
        btn.classList.add('is-selected');

        if (specialSize === '4lbs') {
            // 4lbs mode: use 2lbs variant with quantity 2
            this.is4lbs = true;
            this.setQuantity(2);

            // Find the 2lbs option value (English: 2lb/2 lbs, French: 907g)
            const twoLbBtn = Array.from(this.querySelectorAll('.fs-options--size .fs-option-card'))
                .find(b => {
                    const v = b.dataset.optionValue;
                    return v && (v.includes('2lb') || v.includes('2 lb') || v.includes('2lbs') || v.includes('2 lbs') || v.includes('907g') || v.includes('907 g'));
                });

            if (twoLbBtn) {
                this.selectedSize = twoLbBtn.dataset.optionValue;
            }

            console.log('[FuelStation] 4lbs mode enabled, using size:', this.selectedSize);
        } else {
            this.is4lbs = false;
            this.selectedSize = value;
            this.setQuantity(1);
            console.log('[FuelStation] Selected size:', value);
        }

        this.updateVariantFromSelection();
    }

    // =========================================
    // QUANTITY
    // =========================================

    adjustQuantity(delta) {
        const input = this.querySelector('.fs-quantity__input');
        if (!input) return;

        let value = parseInt(input.value) || 1;
        value = Math.max(1, Math.min(10, value + delta));

        // In 4lbs mode, quantity must be at least 2
        if (this.is4lbs && value < 2) {
            value = 2;
        }

        input.value = value;

        // Refresh price display
        if (this.currentBasePrice) {
            this.updatePriceDisplay(this.currentBasePrice);
        }
    }

    setQuantity(qty) {
        const input = this.querySelector('.fs-quantity__input');
        if (input) {
            input.value = qty;

            // Refresh price display
            if (this.currentBasePrice) {
                this.updatePriceDisplay(this.currentBasePrice);
            }
        }
    }

    // =========================================
    // VARIANT SELECTION
    // =========================================

    updateVariantFromSelection() {
        if (!this.productsData?.products?.[this.currentHandle]) return;

        const productData = this.productsData.products[this.currentHandle];
        this.updateVariant(productData);
    }

    updateVariant(productData) {
        if (!productData?.variants) return;

        // Find matching variant
        const matchedVariant = productData.variants.find(v => {
            const opts = [v.option1, v.option2, v.option3].filter(Boolean);

            // Check if grind matches (if selected)
            const grindMatch = !this.selectedGrind || opts.some(o =>
                o.toLowerCase().includes(this.selectedGrind.toLowerCase()) ||
                this.selectedGrind.toLowerCase().includes(o.toLowerCase())
            );

            // Check if size matches (if selected)
            const sizeMatch = !this.selectedSize || opts.some(o =>
                o.toLowerCase().includes(this.selectedSize.toLowerCase()) ||
                this.selectedSize.toLowerCase().includes(o.toLowerCase())
            );

            return grindMatch && sizeMatch;
        });

        if (matchedVariant) {
            console.log('[FuelStation] Matched variant:', matchedVariant.id, matchedVariant.title);

            // Update hidden input
            const variantInput = this.querySelector('input[name="id"]');
            if (variantInput) {
                variantInput.value = matchedVariant.id;
            }

            // Update base price and refresh displays
            this.currentBasePrice = matchedVariant.price;
            this.updatePriceDisplay(matchedVariant.price);
        } else {
            console.warn('[FuelStation] No matching variant found for:', {
                grind: this.selectedGrind,
                size: this.selectedSize
            });
            // Fall back to first available variant
            const fallback = productData.variants.find(v => v.available) || productData.variants[0];
            if (fallback) {
                const variantInput = this.querySelector('input[name="id"]');
                if (variantInput) {
                    variantInput.value = fallback.id;
                }
                // Update price for fallback
                this.currentBasePrice = fallback.price;
                this.updatePriceDisplay(fallback.price);
            }
        }
    }

    // =========================================
    // AUTOSHIP / SUBSCRIPTION
    // =========================================

    handleAutoshipToggle(e) {
        const isChecked = e.target.checked;
        this.autoshipEnabled = isChecked;

        const widget = this.querySelector('.fs-autoship');
        if (widget) {
            widget.classList.toggle('is-active', isChecked);
        }

        const label = this.querySelector('.fs-autoship__label');
        if (label) {
            label.textContent = isChecked ? e.target.dataset.labelOn : e.target.dataset.labelOff;
        }

        const planSelect = this.querySelector('.fs-autoship__frequency-select');
        if (planSelect) {
            planSelect.disabled = !isChecked;
            if (isChecked && planSelect.options.length > 0) {
                planSelect.selectedIndex = 0; // Select first actual plan
            }
        }

        // Toggle discount text visibility
        const discountNote = this.querySelector('.fs-autoship__discount');
        if (discountNote) {
            discountNote.style.display = isChecked ? 'block' : 'none';
        }

        // Refresh all price displays
        if (this.currentBasePrice) {
            this.updatePriceDisplay(this.currentBasePrice);
        }

        console.log('[FuelStation] Autoship:', isChecked ? 'ON' : 'OFF');
    }

    updateSellingPlans(productData) {
        const planSelect = this.querySelector('.fs-autoship__frequency-select');
        if (!planSelect) return;

        // Clear all existing options
        while (planSelect.options.length > 0) {
            planSelect.remove(0);
        }

        // Add plans from new product - Loop through ALL groups
        if (productData.sellingPlanGroups?.length) {
            productData.sellingPlanGroups.forEach(group => {
                group.selling_plans?.forEach(plan => {
                    const option = document.createElement('option');
                    option.value = plan.id;
                    option.textContent = plan.name;
                    planSelect.appendChild(option);
                });
            });
        }

        // Reset selection if autoship is on
        if (this.autoshipEnabled && planSelect.options.length > 0) {
            planSelect.selectedIndex = 0;
        }
    }

    updatePriceDisplay(basePrice) {
        const qtyInput = this.querySelector('.fs-quantity__input');
        const quantity = qtyInput ? parseInt(qtyInput.value) || 1 : 1;

        const discountFactor = this.autoshipEnabled ? 0.85 : 1.0;
        const finalPrice = Math.round(basePrice * quantity * discountFactor);

        // 1. Update main price display
        const priceEl = this.querySelector('.fs-price__amount');
        if (priceEl) {
            priceEl.textContent = this.formatMoney(finalPrice);
        }

        // 2. Update widget price (mini display)
        const subPriceEl = this.querySelector('.fs-autoship__price');
        if (subPriceEl) {
            // Display only the number, the currency part is separate in Liquid
            const formatted = this.formatMoney(finalPrice).replace('$', '');
            subPriceEl.textContent = formatted;
        }

        // 3. Update Label (PER BAG vs TOTAL)
        const unitLabel = this.querySelector('.fs-autoship__per-unit');
        if (unitLabel) {
            unitLabel.textContent = quantity > 1 ? `TOTAL (${quantity} BAGS)` : 'PER BAG';
        }
    }

    // =========================================
    // FORM SUBMISSION
    // =========================================

    handleSubmit(e) {
        e.preventDefault();

        const form = e.target;
        const variantId = this.querySelector('input[name="id"]')?.value;
        const quantity = this.querySelector('.fs-quantity__input')?.value || 1;

        if (!variantId) {
            console.error('[FuelStation] No variant ID');
            return;
        }

        const formData = {
            id: parseInt(variantId),
            quantity: parseInt(quantity)
        };

        // Add selling plan if autoship is enabled
        if (this.autoshipEnabled) {
            const planSelect = document.getElementById(`fs-selling-plan-${this.sectionId}`);
            if (planSelect?.value) {
                formData.selling_plan = parseInt(planSelect.value);
            }
        }

        console.log('[FuelStation] Adding to cart:', formData);

        const btn = form.querySelector('.fs-add-to-cart');
        if (btn) {
            btn.disabled = true;
            btn.textContent = 'Adding...';
        }

        // Submit via AJAX
        fetch(window.Shopify?.routes?.root ? `${window.Shopify.routes.root}cart/add.js` : '/cart/add.js', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ items: [formData] })
        })
            .then(response => {
                if (!response.ok) {
                    return response.json().then(err => Promise.reject(err));
                }
                return response.json();
            })
            .then(data => {
                console.log('[FuelStation] Cart add success:', data);

                // Refresh and open cart drawer (Impact Theme pattern)
                this.refreshCartDrawer();
            })
            .catch(error => {
                console.error('[FuelStation] Cart error:', error);
                alert(error.description || error.message || 'There was an error adding to cart. Please try again.');

                if (btn) {
                    btn.disabled = false;
                    btn.textContent = 'Add to cart';
                }
            });
    }

    async refreshCartDrawer() {
        // Trigger standard cart refresh events
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        document.dispatchEvent(new CustomEvent('cart:updated'));

        try {
            // Impact Theme: Fetch cart drawer section HTML
            const cartDrawerUrl = (window.Shopify?.routes?.root || '/') + '?section_id=cart-drawer';
            const res = await fetch(cartDrawerUrl);

            if (res.ok) {
                const text = await res.text();
                const parser = new DOMParser();
                const doc = parser.parseFromString(text, 'text/html');
                const newDrawer = doc.querySelector('cart-drawer');
                const currentDrawer = document.querySelector('cart-drawer');

                if (newDrawer && currentDrawer) {
                    currentDrawer.innerHTML = newDrawer.innerHTML;

                    // Open the drawer
                    if (typeof currentDrawer.show === 'function') {
                        currentDrawer.show();
                    } else if (typeof currentDrawer.open === 'function') {
                        currentDrawer.open();
                    } else {
                        currentDrawer.setAttribute('open', '');
                    }

                    console.log('[FuelStation] Cart drawer refreshed and opened');
                }
            }

            // Reset button
            const btn = this.querySelector('.fs-add-to-cart');
            if (btn) {
                btn.disabled = false;
                btn.textContent = 'Add to cart';
            }
        } catch (e) {
            console.warn('[FuelStation] Cart drawer refresh failed:', e);
            // Fallback: redirect to cart
            window.location.href = '/cart';
        }
    }

    // =========================================
    // UTILITIES
    // =========================================

    handleDescriptionToggle(e) {
        const btn = e.currentTarget;
        const wrapper = btn.closest('.fs-description-wrapper');
        const desc = wrapper?.querySelector('.fs-description');

        if (desc && btn) {
            const isExpanded = desc.classList.toggle('is-clamped');
            // Class was toggled OFF (is-clamped removed) -> Expanded
            // Class was toggled ON (is-clamped added) -> Clamped

            // Wait, toggle returns NEW state. If it was removed, it returns false.
            // Let's be explicit:
            const nowClamped = desc.classList.contains('is-clamped');
            const readMoreLabel = btn.dataset.readMore || 'Read More';
            const readLessLabel = btn.dataset.readLess || 'Read Less';
            btn.textContent = nowClamped ? readMoreLabel : readLessLabel;
        }
    }

    formatMoney(cents) {
        // Shopify stores prices in cents
        const dollars = (cents / 100).toFixed(2);
        return `$${dollars}`;
    }
}

// Register custom element
if (!customElements.get('fuel-station-page')) {
    customElements.define('fuel-station-page', FuelStationPage);
}
